import { useMutation, useQueryClient } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ContentComparisonApiService } from 'src/services/contentComparisonApi'
import {
  ContentComparisonCreatePayload,
  ContentComparisonListResponse,
} from 'src/services/contentComparisonApi/interfaces/contentComparison.interface'

const LIST_KEY = [QueryKey.GET_CONTENT_COMPARISONS]

export const useCreateContentComparisonMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ContentComparisonCreatePayload) =>
      ContentComparisonApiService.create(payload),
    // The new row is the list's own content.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

export const useDeleteContentComparisonMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (comparisonId: string) =>
      ContentComparisonApiService.remove(comparisonId),

    // Rolled back if the request fails, so a refused delete puts the row back
    // rather than leaving the list claiming something that still exists.
    onError: (_error, _comparisonId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(LIST_KEY, context.previous)
      }
    },

    // The row leaves the list on click rather than after the round trip: the
    // request can take seconds against a remote database, and waiting for it
    // makes Delete look like it has done nothing.
    onMutate: async (comparisonId: string) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY })

      const previous =
        queryClient.getQueryData<ContentComparisonListResponse>(LIST_KEY)

      queryClient.setQueryData<ContentComparisonListResponse>(
        LIST_KEY,
        (current) =>
          current && {
            ...current,
            data: current.data.filter(
              (comparison) => comparison.id !== comparisonId
            ),
          }
      )

      return { previous }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  })
}
